import React from 'react';
import { AppBar, Box, Toolbar, Typography, IconButton, Badge, Avatar, Menu, MenuItem, useTheme, styled } from '@mui/material';
import { Notifications, Person, Logout, Store as StoreIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'white',
  color: theme.palette.text.primary,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const Logo = styled('img')({
  height: 40,
  marginRight: 16,
});

const AppTitle = styled(Typography)({
  fontWeight: 600,
  background: 'linear-gradient(90deg, #025B29 0%, #028a3e 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  variant: 'h6',
  component: 'div'
} as const);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Implement logout logic
    handleClose();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StyledAppBar position="static">
        <Toolbar>
          <Box 
            component="button" 
            onClick={() => navigate('/')}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1, 
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              '&:hover': {
                opacity: 0.9,
                '& .MuiTypography-root': {
                  background: 'linear-gradient(90deg, #01431e 0%, #027835 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <Logo src="/images/logo.png" alt="Store Check-in Logo" />
            <AppTitle>
              Store Check-in
            </AppTitle>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              color="primary" 
              onClick={() => navigate('/')}
              sx={{ 
                bgcolor: 'rgba(2, 91, 41, 0.1)',
                '&:hover': { 
                  bgcolor: 'rgba(2, 91, 41, 0.2)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <StoreIcon />
            </IconButton>
            {/* Notifications button hidden as per user request
            <IconButton 
              color="primary" 
              onClick={() => navigate('/notifications')}
              sx={{ 
                bgcolor: 'rgba(2, 91, 41, 0.1)',
                '&:hover': { 
                  bgcolor: 'rgba(2, 91, 41, 0.2)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out',
                display: 'none' // Hide the button
              }}
            >
              <Badge badgeContent={4} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            */}
            <IconButton
              size="large"
              edge="end"
              onClick={handleMenu}
              sx={{ 
                ml: 1,
                '&:hover': { bgcolor: 'rgba(2, 91, 41, 0.1)' }
              }}
            >
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}>
                <Person />
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
              <Person sx={{ mr: 1 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </StyledAppBar>
      <Box component="main" sx={{ 
        flexGrow: 1, 
        p: { xs: 2, sm: 3 },
        maxWidth: 1440,
        width: '100%',
        mx: 'auto',
        boxSizing: 'border-box'
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
